import * as anchor from "@project-serum/anchor";
import { Token, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import {
  PublicKey,
  SystemProgram,
  TransactionInstruction,
} from "@solana/web3.js";
import BN from "bn.js";
import { MerkleTree } from "./merkleTree";
import {
  getAtaForMint,
  SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
} from "./utils";

export const GUMDROP_DISTRIBUTOR_ID = new PublicKey(
  "gdrpGjVffourzkdDRrQmySw4aTHr8a3xmQzzxSwFD1a"
);

export interface GumdropInfo {
  distributor: string;
  method: string;
  handle: string;
  amount: number;
  index: number;
  proof: string;
  pin: string;
  candy: string;
  tokenAcc: string;
}

// eslint-disable-next-line
export function getGumdropInfo(handle: string): GumdropInfo | undefined {
  // const result = list.find((l) => l.handle === handle);
  return;
}

export const chunk = (arr: Buffer, len: number): Array<Buffer> => {
  const chunks: Array<Buffer> = [];
  const n = arr.length;
  let i = 0;

  while (i < n) {
    chunks.push(arr.slice(i, (i += len)));
  }

  return chunks;
};

const walletKeyOrPda = async (
  walletKey: PublicKey,
  handle: string,
  pin: BN | null,
  seed: PublicKey
): Promise<[PublicKey, Array<Buffer>]> => {
  if (pin === null) {
    try {
      const key = new PublicKey(handle);
      if (!key.equals(walletKey)) {
        throw new Error(
          "Claimant wallet handle does not match connected wallet"
        );
      }
      return [key, []];
    } catch (err) {
      throw new Error(`Invalid claimant wallet handle ${err}`);
    }
  } else {
    const seeds = [
      seed.toBuffer(),
      Buffer.from(handle),
      Buffer.from(pin.toArray("le", 4)),
    ];

    const [claimantPda] = await PublicKey.findProgramAddress(
      [seeds[0], ...chunk(seeds[1], 32), seeds[2]],
      GUMDROP_DISTRIBUTOR_ID
    );
    return [claimantPda, seeds];
  }
};

export async function getProgram(
  connection: anchor.web3.Connection,
  wallet: anchor.Wallet
) {
  const provider = new anchor.Provider(connection, wallet, {
    preflightCommitment: "recent",
  });
  const [gumdropIdl] = await Promise.all([
    anchor.Program.fetchIdl(GUMDROP_DISTRIBUTOR_ID, provider),
  ]);

  if (!gumdropIdl) throw new Error("Failed to fetch gumdrop IDL");

  return new anchor.Program(gumdropIdl, GUMDROP_DISTRIBUTOR_ID, provider);
}

export const fetchDistributor = async (
  program: anchor.Program,
  distributorStr: string
) => {
  let key;
  try {
    key = new PublicKey(distributorStr);
  } catch (err) {
    throw new Error(`Invalid distributor key ${err}`);
  }
  const info = await program.account.merkleDistributor.fetch(key);
  return [key, info];
};

export function getClaimStatus(index: number, distributorKey: PublicKey) {
  return PublicKey.findProgramAddress(
    [
      Buffer.from("ClaimStatus"),
      Buffer.from(new BN(index).toArray("le", 8)),
      distributorKey.toBuffer(),
    ],
    GUMDROP_DISTRIBUTOR_ID
  );
}

export async function hasBeenClaimed(
  connection: anchor.web3.Connection,
  claimStatus: PublicKey
) {
  return (await connection.getAccountInfo(claimStatus)) !== null;
}

export async function buildCandyClaim(
  whitelistMint: PublicKey,
  program: anchor.Program,
  walletKey: PublicKey,
  distributorKey: PublicKey,
  distributorInfo: any,
  tokenAcc: string,
  proof: Array<Buffer>,
  handle: string,
  amount: number,
  index: number,
  pin: BN | null
): Promise<TransactionInstruction[]> {
  let tokenAccKey: PublicKey;
  try {
    tokenAccKey = new PublicKey(tokenAcc);
  } catch (err) {
    throw new Error(`Invalid tokenAcc key ${err}`);
  }

  const connection = program.provider.connection;

  const [secret /*, pdaSeeds*/] = await walletKeyOrPda(
    walletKey,
    handle,
    pin,
    whitelistMint
  );

  // TODO: since it's in the PDA do we need it to be in the leaf?
  const leaf = Buffer.from([
    ...new BN(index).toArray("le", 8),
    ...Array.from(secret.toBuffer()),
    ...Array.from(whitelistMint.toBuffer()),
    ...new BN(amount).toArray("le", 8),
  ]);

  const matches = MerkleTree.verifyClaim(
    leaf,
    proof,
    Buffer.from(distributorInfo.root)
  );

  if (!matches) {
    throw new Error("Gumdrop merkle proof does not match");
  }

  const [claimStatus, cbump] = await getClaimStatus(index, distributorKey);

  // candy machine mints fit in a single transaction
  const merkleClaim: Array<TransactionInstruction> = [];

  if (!(await hasBeenClaimed(connection, claimStatus))) {
    // atm the contract has a special case for when the temporal key is defaulted
    // (aka always passes temporal check)
    // TODO: more flexible
    const temporalSigner =
      distributorInfo.temporal.equals(PublicKey.default) ||
      secret.equals(walletKey)
        ? walletKey
        : distributorInfo.temporal;

    const walletTokenKey = (await getAtaForMint(whitelistMint, walletKey))[0];

    if ((await connection.getAccountInfo(walletTokenKey)) === null) {
      merkleClaim.push(
        Token.createAssociatedTokenAccountInstruction(
          SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
          TOKEN_PROGRAM_ID,
          whitelistMint,
          walletTokenKey,
          walletKey,
          walletKey
        )
      );
    }

    merkleClaim.push(
      await program.instruction.claim(
        cbump,
        new BN(index),
        new BN(amount),
        secret,
        proof,
        {
          accounts: {
            distributor: distributorKey,
            claimStatus,
            from: tokenAccKey,
            to: walletTokenKey,
            temporal: temporalSigner,
            payer: walletKey,
            systemProgram: SystemProgram.programId,
            tokenProgram: TOKEN_PROGRAM_ID,
          },
        }
      )
    );
  }
  return merkleClaim;
}
